import { Avatar, ParsleyLogo } from '@parsleyai/ui'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import FeedbackTrigger from '@/components/feedback/FeedbackTrigger'
import HeaderUndoControl from '@/components/HeaderUndoControl'
import MobileAvatarSheet from '@/components/MobileAvatarSheet'
import { PlanPill } from '@/components/planGen/PlanPill'
import { parentPath, showMobileBackButton } from '@/lib/mobilePageTitle'
import { useHideOnScroll } from '@/lib/useHideOnScroll'
import { useIsMobile } from '@/lib/useIsMobile'
import { useWhatsNewIndicator } from '@/pages/whatsNew/useWhatsNewIndicator'
import { useAuth, useFeedbackNudge } from '@/store'

/**
 * Ghost top bar for mobile pages: logo or back button left, avatar right. Desktop uses the green Navbar.
 * Hide-on-scroll quick-return — slides up on scroll-down, snaps back on scroll-up. Disabled above md.
 */
export default function GhostTopBar() {
  const { isAuthenticated, user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [sheetOpen, setSheetOpen] = useState(false)
  const isMobile = useIsMobile()
  const { open: nudgeOpen } = useFeedbackNudge()
  // Pinned while the feedback nudge is open — it's anchored to an icon in this bar.
  const { hidden, scrolled } = useHideOnScroll(isMobile && !nudgeOpen)
  const { unseen: whatsNewUnseen } = useWhatsNewIndicator()

  const upPath = showMobileBackButton(location.pathname) ? parentPath(location.pathname) : null

  return (
    <>
      <div
        className={`fixed top-0 left-0 right-0 z-40 md:hidden px-4 bg-background/[0.82] backdrop-blur-nav border-b border-foreground/[0.08] transition duration-200 ease-out will-change-transform motion-reduce:transition-none ${hidden ? '-translate-y-full' : 'translate-y-0'} ${scrolled ? 'shadow-card' : ''}`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center justify-between h-14">
          {upPath ? (
            <button
              type="button"
              onClick={() => navigate(upPath)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground/60 hover:text-foreground hover:bg-foreground/5 transition-colors flex-shrink-0"
              aria-label="Go back">
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <Link
              to="/home"
              className="flex items-center gap-2 flex-shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background">
              <ParsleyLogo tone="green" size="sm" />
            </Link>
          )}

          {isAuthenticated && (
            <div className="flex items-center gap-2">
              {/* Rendered here on mobile only; the desktop Navbar renders it otherwise. Exactly one
                  mount — the popover state is shared via the provider, so a hidden second mount would
                  fight the visible one's click-outside handling. */}
              {isMobile && <PlanPill />}
              <HeaderUndoControl compact />
              <FeedbackTrigger compact />
              <Avatar
                interactive
                onClick={() => setSheetOpen(true)}
                src={user?.photoURL}
                name={user?.displayName}
                size="sm"
                status={whatsNewUnseen ? 'attention' : undefined}
                aria-label="Open account menu"
              />
            </div>
          )}
        </div>
      </div>
      {isAuthenticated && <MobileAvatarSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />}
    </>
  )
}
